import { useEffect, useState } from "react";
import { IList } from "../../types/list.type";
import { api } from "../../api/api";
import {
  Button,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Typography,
} from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useAuth } from "../../contexts/auth.context";
import { UserRole } from "../../types/user.type";
import { canEditLists } from "../../utils/roleUtils";

const { Title } = Typography;
const { Search } = Input;

export default function Lists() {
  const { authState } = useAuth();
  const [lists, setLists] = useState<IList[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedList, setSelectedList] = useState<IList | null>(null);
  const [form] = Form.useForm();

  const fetchLists = async () => {
    setLoading(true);

    try {
      const response = await api.get("/list", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
        },
      });

      setLists(response.data.lists);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching lists", error);
      message.error("Ошибка при получение листов.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLists();
  }, [page, searchTerm, pageSize]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleOpenModal = (list?: IList) => {
    if (list) {
      setSelectedList(list);
    }
    setIsModalOpen(true);
    form.setFieldsValue(list);
  };

  const handleSubmitForm = async (values: { name: string; url: string }) => {
    setConfirmLoading(true);

    try {
      if (selectedList) {
        await api.put(`/list/${selectedList._id}`, values);
        message.success("Лист успешно обновлен.");
      } else {
        await api.post("/list", values);
        message.success("Лист успешно добавлен.");
      }
      fetchLists();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving list", error);
      message.error(error.response.data.error);
    }
  };

  const handleDelete = async (listId: string) => {
    try {
      await api.delete(`/list/${listId}`);
      message.success("Лист успешно удален.");
    } catch (error: any) {
      console.error("Error deleting list", error);
      message.error(error.response.data.error);
    }
  };

  const columns = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Пользователь",
      dataIndex: "ownerName",
      key: "ownerName",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, list: IList) => {
        const menu = (
          <Menu>
            <Menu.Item onClick={() => window.open(list.url, "_blank")}>
              Открыть
            </Menu.Item>
            {canEditLists(authState.user?.role) && (
              <>
                <Menu.Item onClick={() => handleOpenModal(list)}>
                  Изменить
                </Menu.Item>
                <Popconfirm
                  title="Вы уверены, что хотите удалить этот лист?"
                  okText="Да"
                  cancelText="Нет"
                  onConfirm={() => handleDelete(list._id)}
                >
                  <Menu.Item>Удалить</Menu.Item>
                </Popconfirm>
              </>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title>Листы</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          placeholder="Поиск по названию"
          onSearch={handleSearch}
          allowClear
        />
        {canEditLists(authState.user?.role) && (
          <Button type="primary" onClick={() => handleOpenModal()}>
            Добавить
          </Button>
        )}
      </Space>

      <Table
        columns={columns}
        dataSource={lists}
        rowKey={(record) => record._id}
        loading={loading}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
        }}
        scroll={{ x: "max-content" }}
      />

      <Modal
        title={selectedList ? "Изменить лист" : "Добавить лист"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item
            name="name"
            label="Название"
            rules={[{ required: true, message: "Введите Название" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="url"
            label="Ссылка"
            rules={[{ required: true, message: "Введите ссылку" }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
